import { API_CONFIG } from '../config/api';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

/** Decode JWT exp (seconds) without verifying signature — client-side expiry hint only. */
function getAccessTokenExpMs(token: string): number | null {
    try {
        const payloadPart = token.split('.')[1];
        if (!payloadPart) return null;
        const payload = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
}

export function getStoredAccessToken(): string | null {
    try {
        return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch {
        return null;
    }
}

/**
 * Refresh access token using the long-lived funnel refresh token.
 * Returns the new access token or null when refresh is not possible.
 */
export async function refreshAccessToken(): Promise<string | null> {
    let refreshToken: string | null = null;
    try {
        refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
        return null;
    }
    if (!refreshToken) return null;

    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REFRESH_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
        // A rejected refresh will never start working. Drop it so the next
        // visit signs in from email capture instead of reusing a dead token.
        if (response.status === 401 || response.status === 403) {
            try {
                localStorage.removeItem(ACCESS_TOKEN_KEY);
                localStorage.removeItem(REFRESH_TOKEN_KEY);
            } catch { /* storage disabled */ }
        }
        return null;
    }

    const body = await response.json().catch(() => ({}));
    const nextToken = body?.data?.accessToken;
    if (!body?.success || !nextToken) return null;

    try {
        localStorage.setItem(ACCESS_TOKEN_KEY, nextToken);
    } catch {
        return null;
    }
    return nextToken;
}

/**
 * Ensure a usable access token before authenticated post-payment API calls.
 * Refreshes when missing, expired, or expiring within the next 2 minutes.
 */
export async function ensureFunnelAccessToken(): Promise<boolean> {
    const existing = getStoredAccessToken();
    const expMs = existing ? getAccessTokenExpMs(existing) : null;
    const needsRefresh = !existing || (expMs !== null && expMs <= Date.now() + 2 * 60 * 1000);

    if (!needsRefresh) return true;
    return (await refreshAccessToken()) !== null;
}

export function buildAuthHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...extra,
    };
    const token = getStoredAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * fetch that retries once after refreshing the access token on 401.
 */
export async function fetchWithAuthRetry(
    url: string,
    init: RequestInit = {}
): Promise<Response> {
    const withAuth = (): RequestInit => ({
        ...init,
        headers: buildAuthHeaders(
            init.headers && !(init.headers instanceof Headers)
                ? (init.headers as Record<string, string>)
                : undefined
        ),
    });

    let response = await fetch(url, withAuth());
    if (response.status !== 401) return response;

    const refreshed = await refreshAccessToken();
    if (!refreshed) return response;

    return fetch(url, withAuth());
}
